import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

/**
 * Light / dark theme provider.
 *
 *  - Initial value is read from <html data-theme="…">, which is set by the
 *    inline script in public/index.html *before* React mounts. That script
 *    honors localStorage first, otherwise defaults to 'dark'. The brand
 *    default is dark; light is opt-in via the navbar toggle.
 *  - Toggling persists the user's choice to localStorage and updates the
 *    data-theme attribute on the html element. CSS variables defined on
 *    [data-theme="light"] take over from there.
 *  - System preference is intentionally ignored — dark is the default.
 */

const STORAGE_KEY = 'kp-theme';

const ThemeContext = createContext({
  theme: 'dark',
  setTheme: () => {},
  toggleTheme: () => {},
});

const readInitialTheme = () => {
  if (typeof document === 'undefined') return 'dark';
  const fromAttr = document.documentElement.dataset.theme;
  if (fromAttr === 'light' || fromAttr === 'dark') return fromAttr;
  return 'dark';
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(readInitialTheme);

  // Push theme to <html> + localStorage whenever it changes.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch (e) {
      /* localStorage may be unavailable (private mode etc.) — non-fatal */
    }
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (next !== 'light' && next !== 'dark') return;
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((curr) => (curr === 'dark' ? 'light' : 'dark'));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;
